// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import * as z from 'zod';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { costSchema } from '@/costs/api/types';
import { Currency } from '@/currency/api/types';
import { serializeOptionalCurrencyForAPI } from '@/currency/utilities/serialize';
import { serializeDateForAPI } from '@/utilities/date-time';
import { DateTime } from 'luxon';

const updateCostResponseSchema = z
  .object({
    data: costSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, cost: data }));

type UpdateCostResponse = z.infer<typeof updateCostResponseSchema>;

interface UpdateCostParams {
  amount?: Currency | null;
  description?: string;
  costIncurredOn?: DateTime;
  category?: string;
  projectId?: string | null;
  personId?: string | null;
}

/**
 * Updates an existing cost using the new direct cost endpoint.
 * This should be used for non-time costs.
 *
 * @param costKey - The cost key to update
 * @param params - The cost update parameters
 */
export async function updateCost(
  costKey: string,
  params: UpdateCostParams
): Promise<UpdateCostResponse> {
  const url = `/v1/cost/${costKey}`;
  const response = await api.patch(url, {
    amount: serializeOptionalCurrencyForAPI(params.amount),
    description: params.description,
    costIncurredOn: serializeDateForAPI(params.costIncurredOn),
    category: params.category,
    projectId: params.projectId,
    personId: params.personId,
  });

  return parseResponse(updateCostResponseSchema, response.data, {
    schemaName: 'UpdateCost',
    endpoint: url,
  });
}
